import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  ShoppingCart, 
  ArrowLeft, 
  Plus, 
  Search,
  Trash2,
  Loader2,
  Package,
  Building2,
  TrendingDown,
  Check,
  PackagePlus,
  ChevronsUpDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { dualPriceText } from "@/components/DualPrice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewOrderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const preselectedSupplier = searchParams.get('supplier');
  const preselectedEan = searchParams.get('ean');
  
  // Get preselected product from navigation state (from PricesPage)
  const preselectedProduct = location.state?.preselectedProduct;
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Order items
  const [orderItems, setOrderItems] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(11.50);
  const [expectedWeek, setExpectedWeek] = useState(() => {
    // Default to current week
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  });
  
  // Product search (Step 1)
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Create new product dialog
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    artikelnummer: "",
    ean: "",
    kategori: "",
    tillverkare: ""
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  
  // Combobox open states
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [manufacturerOpen, setManufacturerOpen] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Check for scanned items from invoice scan
  useEffect(() => {
    const fromScan = searchParams.get('from') === 'scan';
    if (fromScan && !loading && suppliers && suppliers.length > 0) {
      const scannedItems = sessionStorage.getItem('createOrderItems');
      const scannedInfo = sessionStorage.getItem('createOrderInfo');
      
      if (scannedItems) {
        try {
          const items = JSON.parse(scannedItems);
          const info = scannedInfo ? JSON.parse(scannedInfo) : {};
          
          // Find matching supplier
          let supplierId = "";
          if (info.matched_supplier?.id) {
            supplierId = info.matched_supplier.id;
          } else if (info.supplier_info?.company_name && suppliers) {
            const match = suppliers.find(s => 
              s.company_name?.toLowerCase().includes(info.supplier_info.company_name.toLowerCase()) ||
              info.supplier_info.company_name.toLowerCase().includes(s.company_name?.toLowerCase() || "")
            );
            if (match) supplierId = match.id;
          }
          
          // Convert scanned items to order items
          const convertedItems = items.map(item => ({
            id: `scanned-${Math.random().toString(36).substr(2, 9)}`,
            ean: item.ean || item.system_ean || "",
            artikelnummer: item.artikelnummer || item.system_artikelnummer || "",
            produktnamn: item.produktnamn || item.system_produktnamn || "",
            quantity: item.quantity || 1,
            supplier_id: supplierId,
            unit_price: item.unit_price || 0,
            currency: "SEK"
          }));
          
          setOrderItems(convertedItems);
          
          toast.success(`${convertedItems.length} produkter importerade från fakturaskanning`);
          
          // Clear session storage
          sessionStorage.removeItem('createOrderItems');
          sessionStorage.removeItem('createOrderInfo');
        } catch (e) {
          console.error("Error parsing scanned items:", e);
        }
      }
    }
  }, [loading, suppliers, searchParams]);

  const fetchInitialData = async () => {
    try {
      const [suppliersRes, categoriesRes, manufacturersRes, rateRes] = await Promise.all([
        axios.get(`${API}/purchasing/suppliers`),
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/manufacturers`),
        axios.get(`${API}/purchasing/exchange-rate`)
      ]);
      setSuppliers(suppliersRes.data);
      setCategories(categoriesRes.data.filter(c => c && c !== "Välj Kategori"));
      setManufacturers(manufacturersRes.data.filter(m => m && m !== "Välj Tillverkare"));
      setExchangeRate(rateRes.data.rate || 11.50);
      
      // Pre-fill if EAN is provided in URL
      if (preselectedEan) {
        setProductSearch(preselectedEan);
      }
      
      // Handle preselected product from navigation state (from PricesPage)
      if (preselectedProduct) {
        const newItem = {
          id: `preselected-${Math.random().toString(36).substr(2, 9)}`,
          ean: preselectedProduct.ean || "",
          artikelnummer: preselectedProduct.artikelnummer || "",
          produktnamn: preselectedProduct.produktnamn || "",
          quantity: 1,
          supplier_id: preselectedProduct.supplierId || "",
          unit_price: preselectedProduct.price || 0,
          currency: preselectedProduct.currency || "SEK"
        };
        setOrderItems([newItem]);
        toast.success(`${preselectedProduct.artikelnummer} tillagd i beställningen`);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search products with price info
  useEffect(() => {
    if (productSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await axios.get(`${API}/inventory`, {
          params: { search: productSearch, limit: 10 }
        });
        
        // Fetch prices for each product to suggest best supplier
        const productsWithPrices = await Promise.all(
          (response.data.items || []).map(async (product) => {
            const prices = [];
            for (const supplier of suppliers) {
              try {
                const res = await axios.get(`${API}/purchasing/suppliers/${supplier.id}/prices`, {
                  params: { search: product.ean }
                });
                const match = res.data.find(p => p.ean === product.ean);
                if (match) {
                  prices.push({
                    supplier_id: supplier.id,
                    supplier_name: supplier.company_name,
                    price: match.purchase_price,
                    currency: match.currency
                  });
                }
              } catch (e) {
                // Ignore
              }
            }
            
            // Sort by price and get best
            prices.sort((a, b) => a.price - b.price);
            const bestPrice = prices[0] || null;
            
            return {
              ...product,
              prices,
              bestPrice,
              suggestedSupplier: preselectedSupplier || bestPrice?.supplier_id || null
            };
          })
        );
        
        setSearchResults(productsWithPrices);
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [productSearch, suppliers]);

  const addProduct = (product) => {
    if (orderItems.some(i => i.ean === product.ean)) {
      // Increase quantity if already exists
      setOrderItems(prev => prev.map(item => 
        item.ean === product.ean 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success(`Ökade antal för ${product.artikelnummer}`);
    } else {
      // Add new item
      const selectedSupplier = product.suggestedSupplier;
      const priceInfo = product.prices?.find(p => p.supplier_id === selectedSupplier);
      
      setOrderItems(prev => [...prev, {
        ean: product.ean,
        artikelnummer: product.artikelnummer,
        produktnamn: product.produktnamn,
        quantity: 1,
        supplier_id: selectedSupplier,
        unit_price: priceInfo?.price || 0,
        currency: priceInfo?.currency || "SEK",
        customer_order_number: "",
        prices: product.prices,
        bestPrice: product.bestPrice
      }]);
      toast.success(`La till ${product.artikelnummer}`);
    }
    
    setProductSearch("");
    setSearchResults([]);
  };

  const openCreateProductDialog = () => {
    const query = productSearch;
    const isEan = /^\d{8,13}$/.test(query);
    
    setCreateProductForm({
      artikelnummer: isEan ? "" : query,
      ean: isEan ? query : "",
      kategori: "",
      tillverkare: ""
    });
    setShowCreateProduct(true);
  };

  const handleCreateProduct = async () => {
    if (!createProductForm.artikelnummer || !createProductForm.ean) {
      toast.error("Artikelnummer och EAN är obligatoriska");
      return;
    }
    
    setCreatingProduct(true);
    try {
      await axios.post(`${API}/inventory`, createProductForm);
      toast.success(`Produkt ${createProductForm.artikelnummer} skapad!`);
      setShowCreateProduct(false);
      
      // Add the new product to order
      const newProduct = {
        ean: createProductForm.ean,
        artikelnummer: createProductForm.artikelnummer,
        prices: [],
        bestPrice: null,
        suggestedSupplier: preselectedSupplier || null
      };
      
      addProduct(newProduct);
      setCreateProductForm({ artikelnummer: "", ean: "", kategori: "", tillverkare: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa produkt");
    } finally {
      setCreatingProduct(false);
    }
  };

  const updateItem = (ean, field, value) => {
    setOrderItems(prev => prev.map(item => {
      if (item.ean === ean) {
        const updated = { ...item, [field]: value };
        
        // If supplier changed, update price
        if (field === 'supplier_id') {
          const priceInfo = item.prices?.find(p => p.supplier_id === value);
          updated.unit_price = priceInfo?.price || 0;
          updated.currency = priceInfo?.currency || "SEK";
        }
        
        return updated;
      }
      return item;
    }));
  };

  const removeItem = (ean) => {
    setOrderItems(prev => prev.filter(item => item.ean !== ean));
  };

  const handleSubmit = async () => {
    if (orderItems.length === 0) {
      toast.error("Lägg till minst en produkt");
      return;
    }
    
    // Check all items have supplier
    const missingSupplier = orderItems.find(i => !i.supplier_id);
    if (missingSupplier) {
      toast.error(`Välj leverantör för ${missingSupplier.artikelnummer}`);
      return;
    }
    
    // Group items by supplier
    const ordersBySupplier = {};
    orderItems.forEach(item => {
      if (!ordersBySupplier[item.supplier_id]) {
        ordersBySupplier[item.supplier_id] = [];
      }
      ordersBySupplier[item.supplier_id].push(item);
    });
    
    setSaving(true);
    try {
      const createdOrders = [];
      
      for (const [supplierId, items] of Object.entries(ordersBySupplier)) {
        const response = await axios.post(`${API}/purchasing/orders`, {
          supplier_id: supplierId,
          expected_delivery_week: expectedWeek || null,
          items: items.map(i => ({
            ean: i.ean,
            artikelnummer: i.artikelnummer,
            quantity: i.quantity,
            unit_price: i.unit_price,
            currency: i.currency
          }))
        });
        createdOrders.push(response.data.order_number);
      }
      
      if (createdOrders.length === 1) {
        toast.success(`Order ${createdOrders[0]} skapad`);
      } else {
        toast.success(`${createdOrders.length} ordrar skapade: ${createdOrders.join(', ')}`);
      }
      
      navigate('/purchasing/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa order");
    } finally {
      setSaving(false);
    }
  };

  // Convert to SEK for total calculation
  const convertToSEK = (price, currency) => {
    if (currency === "EUR") {
      return price * exchangeRate;
    }
    return price;
  };

  const totalValue = orderItems.reduce((sum, i) => sum + convertToSEK(i.unit_price * i.quantity, i.currency), 0);
  const totalQuantity = orderItems.reduce((sum, i) => sum + i.quantity, 0);

  // Group order items by supplier for summary
  const itemsBySupplier = {};
  orderItems.forEach(item => {
    const supplier = suppliers.find(s => s.id === item.supplier_id);
    const name = supplier?.company_name || 'Ej vald';
    if (!itemsBySupplier[name]) {
      itemsBySupplier[name] = [];
    }
    itemsBySupplier[name].push(item);
  });

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing/orders">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-purple-500" />
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  Ny beställning
                </h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Sök produkter → Leverantör föreslås automatiskt
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Step 1: Search Product */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">1</span>
              Sök produkt
            </CardTitle>
            <CardDescription>
              Systemet föreslår automatiskt leverantör med lägst pris
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök produkt (EAN eller artikelnummer)..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10"
                data-testid="product-search"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />
              )}
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-md mt-2 max-h-80 overflow-y-auto">
                {searchResults.map(product => (
                  <div
                    key={product.ean}
                    className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                    onClick={() => addProduct(product)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold">{product.artikelnummer}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-sm truncate">{product.produktnamn}</span>
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">{product.ean}</p>
                        
                        {/* Best price suggestion */}
                        {product.bestPrice ? (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-green-500 text-white gap-1">
                              <TrendingDown className="h-3 w-3" />
                              Bäst pris
                            </Badge>
                            <span className="text-sm">
                              {product.bestPrice.supplier_name}: {dualPriceText(product.bestPrice.price, product.bestPrice.currency, exchangeRate)}
                            </span>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-2">
                            Inget pris registrerat
                          </p>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" className="shrink-0">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* No results - offer to create product */}
            {productSearch.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="border rounded-md mt-2 p-4 bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Ingen produkt hittades för "{productSearch}"</p>
                    <p className="text-xs text-muted-foreground">Vill du skapa en ny produkt?</p>
                  </div>
                  <Button 
                    onClick={openCreateProductDialog}
                    variant="outline"
                    className="gap-2"
                    data-testid="create-product-btn"
                  >
                    <PackagePlus className="h-4 w-4" />
                    Skapa produkt
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Items */}
        {orderItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading uppercase flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">2</span>
                Beställda produkter ({orderItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {orderItems.map((item) => (
                <Card key={item.ean} className="border-border">
                  <CardContent className="p-3 space-y-3">
                    {/* Product info */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-mono font-bold">{item.artikelnummer}</span>
                        {item.produktnamn && (
                          <span className="text-sm text-muted-foreground ml-2">{item.produktnamn}</span>
                        )}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeItem(item.ean)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Supplier selection */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Leverantör</Label>
                        <Select 
                          value={item.supplier_id || ""}
                          onValueChange={(v) => updateItem(item.ean, 'supplier_id', v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Välj..." />
                          </SelectTrigger>
                          <SelectContent>
                            {suppliers.map(s => {
                              const priceInfo = item.prices?.find(p => p.supplier_id === s.id);
                              const isBest = item.bestPrice?.supplier_id === s.id;
                              return (
                                <SelectItem key={s.id} value={s.id}>
                                  <div className="flex items-center gap-2">
                                    {isBest && <Check className="h-3 w-3 text-green-500" />}
                                    {s.company_name}
                                    {priceInfo && (
                                      <span className="text-muted-foreground">
                                        ({dualPriceText(priceInfo.price, priceInfo.currency, exchangeRate)})
                                      </span>
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {item.bestPrice && item.supplier_id !== item.bestPrice.supplier_id && (
                          <p className="text-xs text-yellow-600">
                            Obs: {item.bestPrice.supplier_name} har lägre pris
                          </p>
                        )}
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs">Antal</Label>
                        <div className="flex items-center gap-1 bg-muted rounded-md h-9">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => updateItem(item.ean, 'quantity', Math.max(1, item.quantity - 1))}
                          >
                            -
                          </Button>
                          <span className="w-10 text-center font-mono font-bold">
                            {item.quantity}
                          </span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => updateItem(item.ean, 'quantity', item.quantity + 1)}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Price info */}
                    {item.unit_price > 0 && (
                      <div className="text-right text-sm">
                        <span className="text-muted-foreground">{item.quantity} × {item.unit_price.toFixed(2)} {item.currency} = </span>
                        <span className="font-bold">{(item.quantity * item.unit_price).toFixed(2)} {item.currency}</span>
                        {item.currency === 'EUR' && (
                          <span className="block text-xs text-muted-foreground">
                            ≈ {Math.round(item.quantity * item.unit_price * exchangeRate).toLocaleString('sv-SE')} kr
                          </span>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Expected Week */}
        {orderItems.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="max-w-xs">
                <Label className="mb-2 block">Förväntad leveransvecka</Label>
                <Select 
                  value={expectedWeek?.toString() || ""} 
                  onValueChange={(v) => setExpectedWeek(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Välj vecka..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {(() => {
                      const now = new Date();
                      const startOfYear = new Date(now.getFullYear(), 0, 1);
                      const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
                      const currentWeek = Math.ceil((days + startOfYear.getDay() + 1) / 7);
                      
                      // Show current week + 10 weeks forward
                      const weeks = [];
                      for (let i = 0; i <= 10; i++) {
                        let weekNum = currentWeek + i;
                        let year = now.getFullYear();
                        
                        // Handle year rollover
                        if (weekNum > 52) {
                          weekNum = weekNum - 52;
                          year = year + 1;
                        }
                        
                        const isCurrentWeek = i === 0;
                        const yearSuffix = year !== now.getFullYear() ? ` (${year})` : "";
                        
                        weeks.push(
                          <SelectItem key={`${year}-${weekNum}`} value={weekNum.toString()}>
                            Vecka {weekNum}{yearSuffix} {isCurrentWeek && "(denna vecka)"}
                          </SelectItem>
                        );
                      }
                      return weeks;
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary & Submit */}
        {orderItems.length > 0 && (
          <Card className="bg-primary/5 border-primary/30">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Sammanfattning</p>
                  <p className="font-bold">
                    {totalQuantity} produkter
                    {Object.keys(itemsBySupplier).length > 1 && 
                      ` hos ${Object.keys(itemsBySupplier).length} leverantörer`
                    }
                  </p>
                  {totalValue > 0 && (
                    <p className="text-sm">
                      Totalt värde: <span className="font-bold">~{Math.round(totalValue)} SEK</span>
                    </p>
                  )}
                  
                  {/* Breakdown by supplier */}
                  {Object.keys(itemsBySupplier).length > 1 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {Object.entries(itemsBySupplier).map(([name, items]) => (
                        <div key={name}>
                          {name}: {items.length} produkter
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handleSubmit} 
                  disabled={saving}
                  className="gap-2"
                  size="lg"
                  data-testid="create-order-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Lägger till...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Lägg till för beställning
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Create Product Dialog */}
      <Dialog open={showCreateProduct} onOpenChange={setShowCreateProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-green-500" />
              Skapa ny produkt
            </DialogTitle>
            <DialogDescription>
              Produkten finns inte i systemet. Fyll i uppgifterna för att skapa den.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Artikelnummer *</Label>
                <Input
                  placeholder="T.ex. ABC-123"
                  value={createProductForm.artikelnummer}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, artikelnummer: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>EAN *</Label>
                <Input
                  placeholder="8-13 siffror"
                  value={createProductForm.ean}
                  onChange={(e) => setCreateProductForm(prev => ({ ...prev, ean: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={categoryOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.kategori || "Välj kategori..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök kategori..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen kategori hittad</CommandEmpty>
                        <CommandGroup>
                          {categories.map((cat) => (
                            <CommandItem
                              key={cat}
                              value={cat}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, kategori: cat }));
                                setCategoryOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.kategori === cat ? "opacity-100" : "opacity-0")} />
                              {cat}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Tillverkare</Label>
                <Popover open={manufacturerOpen} onOpenChange={setManufacturerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={manufacturerOpen}
                      className="w-full justify-between font-normal"
                    >
                      {createProductForm.tillverkare || "Välj tillverkare..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök tillverkare..." />
                      <CommandList className="max-h-48">
                        <CommandEmpty>Ingen tillverkare hittad</CommandEmpty>
                        <CommandGroup>
                          {manufacturers.map((man) => (
                            <CommandItem
                              key={man}
                              value={man}
                              onSelect={() => {
                                setCreateProductForm(prev => ({ ...prev, tillverkare: man }));
                                setManufacturerOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", createProductForm.tillverkare === man ? "opacity-100" : "opacity-0")} />
                              {man}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProduct(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleCreateProduct} 
              disabled={creatingProduct || !createProductForm.artikelnummer || !createProductForm.ean}
              className="gap-2"
            >
              {creatingProduct ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Skapar...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Skapa & lägg till
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
